import {
  IListElementCell,
  getCellControl,
} from 'app/services/api5-service/api.interface';

import {
  ISPFieldConfig,
  ISPFieldWrapper,
  ISPFormState,
} from 'common/dynamic-form/model';
import {
  TypedField,
  getTypedField,
} from 'common/dynamic-form/utils/form-preparing';
import {
  appendConfigWrapperOptions,
  getConfigsFromTypedField,
  setTypedFieldToServices,
} from 'common/dynamic-form/utils/formly-configs-generation';

import { IListCellUi } from '../../list-cell/model';
import { ListConfig } from '../../model';

/**
 * Get a typed field and register this field in all services
 *
 * @param cellData - list cell
 * @param state - dynamic form state
 */
function getTypedFieldForCell(
  cellData: IListElementCell,
  state: ISPFormState,
): TypedField {
  const { type, control } = getCellControl(cellData);
  const field = getTypedField(
    {
      $name: control.$name,
      [type]: [control],
    },
    state.docSubject.value,
  );
  return field;
}

/**
 * Change the state of the form according to the control in the list cell.
 * This state will only apply to the list
 *
 * @param cellData - list cell
 * @param state - dynamic form state
 */
function changeStateDependingOnControl(
  cellData: IListElementCell,
  state: ISPFormState,
): ISPFormState {
  if ('select' in cellData) {
    // in order not to change for the entire form, but to use the changed state only for the list
    const newState: ISPFormState = { ...state };
    newState.selectAnchorWidth = 120;
    newState.selectDropdownWidth = '120px';
    return newState;
  }
  return state;
}

/**
 * Get the config for the available control in the cell (currently select, input, reflect)
 * along with wrapper configs.
 * For inaccessible configs, undefined will be returned
 *
 * @param cell
 * @param cellData - list cell
 * @param state - dynamic form state
 * @param config
 */
function getControlConfigWithWrappers(
  cell: IListCellUi,
  cellData: IListElementCell,
  state: ISPFormState,
  config: ListConfig,
): ISPFieldConfig {
  const existedConfig = config.fieldGroup?.find(c => c.id === cell.id);
  if (existedConfig) {
    return existedConfig;
  }

  const newState = changeStateDependingOnControl(cellData, state);

  const field = getTypedFieldForCell(cellData, newState);

  const cellConfig = getConfigsFromTypedField(field, newState)[0];
  cellConfig.wrappers = cellConfig.wrappers?.filter(
    wrapper => wrapper === ISPFieldWrapper.ValidationError,
  );
  appendConfigWrapperOptions(cellConfig, field.field, newState);

  setTypedFieldToServices(field, newState);

  cellConfig.parent = config;
  cellConfig.id = cell.id;

  state.builder.build(cellConfig);

  return cellConfig;
}

/**
 * Get control cell with specific data
 *
 * @param cell - FE cell base metadata
 * @param cellData - BE meta of list row
 * @param state
 * @param config - list config
 */
export function getControlCellWithData(
  cell: IListCellUi,
  cellData: IListElementCell,
  state: ISPFormState,
  config: ListConfig,
): IListCellUi {
  const cellConfig = getControlConfigWithWrappers(
    cell,
    cellData,
    state,
    config,
  );

  cell.config = cellConfig;
  cell.trackBy = cellConfig.key;
  cell.verticalAlign = cell.verticalAlign || 'middle';
  return cell;
}
